import { makeAutoObservable } from 'mobx'
import { type IAlertProps } from 'shared/ui/Alert/types'
import { type Conversation } from 'entities/Conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class TrialQuestionnaireAlertStore {
  constructor(private _conversation: Conversation, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get trialSubscription() {
    return this._entities.subscriptionStore.trialSubscriptionStore
  }

  get number() {
    return (
      this._entities.numbersStore.getItem(this._conversation?.number_id) ??
      this._conversation?.number
    )
  }

  get stateTollFree() {
    if (this.number?.isStatusBlocked) return 'step_2_reject'
    if (this.number?.isStatusDeclined) return 'step_1_reject'
    if (this.number?.isStatusVerified) return 'step_1_upgrade'
    if (this.number?.isStatusInternalReview || this.number?.isStatusUnderVerification)
      return 'step_2'
    if (
      this.number?.isStatusUnverified &&
      !this.trialSubscription.organizationTollFree?.isStatusEmpty
    )
      return 'step_1'

    return 'default'
  }

  get stateBusinessProfile10DLC() {
    return this.trialSubscription.stateBusinessProfile10DLC
  }

  get state() {
    return this.trialSubscription.state
  }

  get conversationNumberType() {
    return this.trialSubscription.conversationNumberType
  }

  get configToolFree(): IAlertProps | null {
    if (this.stateTollFree === 'step_1_upgrade') return null

    if (this.stateTollFree === 'step_2_reject') {
      return {
        item: {
          type: 'error',
          title: 'Your application was rejected.',
          desc: this.trialSubscription.isAdminOrOwnerRole
            ? 'Please contact us for more information.'
            : 'Ask one of your admins to contact us for more information.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link: '/settings/organization/compliance/toll-free',
                text: 'See details',
              },
            ]
          : undefined,
      }
    }

    if (this.stateTollFree === 'step_1_reject') {
      return {
        item: {
          type: 'error',
          title: 'Your application was rejected.',
          desc: this.trialSubscription.isAdminOrOwnerRole
            ? 'Please update and resubmit to send texts.'
            : 'Ask one of your admins to update and resubmit to send texts.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link: '/settings/organization/compliance/toll-free',
                text: 'See details',
              },
            ]
          : undefined,
      }
    }

    if (this.stateTollFree === 'step_2') {
      return {
        item: {
          type: 'infoBlue',
          title: 'Your business application is under review.',
          desc: 'Once approved, you’ll be able to start texting. In the meantime, calling is ready for you.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link: '/settings/organization/compliance/toll-free',
                text: 'Check status',
              },
            ]
          : undefined,
      }
    }

    if (this.stateTollFree === 'step_1') {
      return {
        item: {
          type: 'infoBlue',
          title: this.trialSubscription.isAdminOrOwnerRole
            ? 'You must complete this process to start texting.'
            : 'One of your admins must complete this process to start texting.',
          desc: 'Once approved, you’ll be able to start texting. In the meantime, calling is ready for you.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link: '/settings/organization/compliance/toll-free',
                text: 'Continue',
              },
            ]
          : undefined,
      }
    }

    return {
      item: {
        type: 'infoBlue',
        title: this.trialSubscription.isAdminOrOwnerRole
          ? 'Get approved to send texts.'
          : 'One of your admins must get approved to send texts.',
        desc: 'Fill out this quick form to get approved by the carriers to send texts.',
      },
      rightActions: this.trialSubscription.isAdminOrOwnerRole
        ? [
            {
              type: 'primary',
              variant: 'navLink',
              appType: 'vue',
              link: '/settings/organization/compliance/toll-free',
              text: 'Get approved',
            },
          ]
        : undefined,
    }
  }

  get configBusinessProfileAnd10DLC(): IAlertProps | null {
    if (this.conversationNumberType === 'pft') {
      return this.configDefault
    }
    if (this._entities.tenDlcLimitsStore.isMessagingCanadaOnly) return null

    if (this.stateBusinessProfile10DLC === 'step_1_upgrade') return null

    if (this.stateBusinessProfile10DLC === 'step_1_reject') {
      return {
        item: {
          type: 'error',
          title: 'Your application was rejected.',
          desc: this.trialSubscription.isAdminOrOwnerRole
            ? 'Please update and resubmit to send texts.'
            : 'Ask one of your admins to update and resubmit to send texts.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                text: 'Update',
                link: this.trialSubscription.businessProfile?.isStatusFailed
                  ? '/settings/organization/compliance/business-profile'
                  : this.trialSubscription.compliance10DLCServicesList?.isStatusFailed
                  ? '/settings/organization/compliance/local-numbers'
                  : '/settings/organization/compliance',
              },
            ]
          : undefined,
      }
    }

    if (
      this.stateBusinessProfile10DLC === 'step_3' ||
      this.stateBusinessProfile10DLC === 'step_4'
    ) {
      return {
        item: {
          type: 'infoBlue',
          title: 'Your business application is under review.',
          desc: 'Once approved, you’ll be able to start texting. In the meantime, calling is ready for you.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link: '/settings/organization/compliance',
                text: 'Check status',
              },
            ]
          : undefined,
      }
    }

    if (
      this.stateBusinessProfile10DLC === 'step_1' ||
      this.stateBusinessProfile10DLC === 'step_2'
    ) {
      return {
        item: {
          type: 'infoBlue',
          title: this.trialSubscription.isAdminOrOwnerRole
            ? 'You must complete this process to start texting.'
            : 'One of your admins must complete this process to start texting.',
          desc: 'Once approved, you’ll be able to start texting. In the meantime, calling is ready for you.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link:
                  this.stateBusinessProfile10DLC === 'step_1'
                    ? '/settings/organization/compliance/business-profile/company-location'
                    : '/settings/organization/compliance/local-numbers/campaign-use-cases',
                text: 'Continue',
              },
            ]
          : undefined,
      }
    }

    if (this.stateBusinessProfile10DLC === 'step_0') {
      return {
        item: {
          type: 'infoBlue',
          title: this.trialSubscription.isAdminOrOwnerRole
            ? 'Get approved to send texts.'
            : 'One of your admins must get approved to send texts.',
          desc: 'Fill out this quick form to get approved by the carriers to send texts.',
        },
        rightActions: this.trialSubscription.isAdminOrOwnerRole
          ? [
              {
                type: 'primary',
                variant: 'navLink',
                appType: 'vue',
                link: '/settings/organization/compliance/business-profile/company-location',
                text: 'Get approved',
              },
            ]
          : undefined,
      }
    }

    return null
  }

  get configDefault(): IAlertProps {
    return {
      item: {
        type: 'infoBlue',
        title: 'This number is just for testing during your free trial.',
        desc: `You’ve got ${this._entities.organizationStore.trialEndsAtString} until your free trial ends.`,
      },
    }
  }

  get config(): IAlertProps | null {
    if (this.state === 'number_local_owner' || this.state === 'number_local_member') {
      return this.configBusinessProfileAnd10DLC
    }

    if (this.state === 'number_tollfree_owner' || this.state === 'number_tollfree_member') {
      if (this.conversationNumberType === 'toll_free') return this.configToolFree

      return null
    }

    if (this.conversationNumberType === 'toll_free') return null
    if (this.conversationNumberType === 'pft') return this.configDefault

    return null
  }

  get hide() {
    if (this.trialSubscription.loading) return true

    return this.trialSubscription.hide
  }
}
